// TabAI.jsx - Fully BYOK AI Gateway for MeMark
// Store user API keys locally in browser localStorage. Zero cloud database leakage.

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { playSound } from '../../lib/soundEffects';

const { FiCpu, FiKey, FiCheck, FiActivity, FiFilter, FiEye, FiEyeOff, FiTrash2, FiExternalLink } = FiIcons;

const PRESET_MODELS = [
  { id: 'openrouter/free', name: 'Auto Failover Free Pool', provider: 'OpenRouter', desc: 'Cycles intelligently through all available zero-cost models.' },
  { id: 'google/gemini-2.0-flash-exp:free', name: 'Google Gemini 2.0 Flash', provider: 'Google AI', desc: 'Extreme speed and multimodal intelligence.' },
  { id: 'meta-llama/llama-3.3-70b-instruct:free', name: 'Meta LLaMA 3.3 70B', provider: 'Meta AI', desc: 'Top tier open weights reasoning and deep context.' },
  { id: 'openai/gpt-4o-mini', name: 'OpenAI GPT-4o Mini', provider: 'OpenAI', desc: 'Fast, precise structured JSON output and tagging.' },
  { id: 'anthropic/claude-3.5-haiku', name: 'Claude 3.5 Haiku', provider: 'Anthropic', desc: 'Lightning fast conversational comprehension.' },
  { id: 'deepseek/deepseek-chat', name: 'DeepSeek V3', provider: 'DeepSeek', desc: 'High capability coding and general synthesis.' },
];

export default function TabAI({ settings, onSettingsChange, theme, accent = '#10B981' }) {
  const { textColor, mutedText, inputBg, inputBorder, surfaceBg, cardBg } = theme;

  const [apiKey, setApiKey] = useState(() => localStorage.getItem('memark_byok_api_key') || '');
  const [showKey, setShowKey] = useState(false);
  const [keySaved, setKeySaved] = useState(false);

  const [selectedModel, setSelectedModel] = useState(() => {
    return localStorage.getItem('memark_ai_model') || 'openrouter/free';
  });

  const [customModel, setCustomModel] = useState(() => {
    return localStorage.getItem('memark_custom_model') || '';
  });

  const [stripThinking, setStripThinking] = useState(() => {
    return localStorage.getItem('memark_strip_thinking') !== 'false';
  });

  const [pingStatus, setPingStatus] = useState(null); // null | 'testing' | { ms: 142, status: 'ok', msg: '' }

  const handleSaveApiKey = () => {
    const trimmed = apiKey.trim();
    localStorage.setItem('memark_byok_api_key', trimmed);
    setKeySaved(true);
    playSound('success');
    setTimeout(() => setKeySaved(false), 2000);
  };

  const handleClearApiKey = () => {
    localStorage.removeItem('memark_byok_api_key');
    setApiKey('');
    playSound('click');
  };

  const handleModelChange = (id) => {
    setSelectedModel(id);
    localStorage.setItem('memark_ai_model', id);
    playSound('click');
  };

  const handleSaveCustomModel = () => {
    const trimmed = customModel.trim();
    if (trimmed) {
      setSelectedModel(trimmed);
      localStorage.setItem('memark_ai_model', trimmed);
      localStorage.setItem('memark_custom_model', trimmed);
      playSound('success');
    }
  };

  const handleToggleStripThinking = () => {
    const next = !stripThinking;
    setStripThinking(next);
    localStorage.setItem('memark_strip_thinking', next ? 'true' : 'false');
    playSound('click');
  };

  const handleTestPing = async () => {
    setPingStatus('testing');
    playSound('click');
    const start = performance.now();

    const userKey = apiKey.trim() || localStorage.getItem('memark_byok_api_key') || '';
    const targetModel = selectedModel || 'openrouter/free';

    if (!userKey) {
      setPingStatus({ ms: 0, status: 'err', msg: 'OpenRouter Key Required: Please input your personal key above first.' });
      playSound('click');
      return;
    }

    try {
      const res = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userKey}`,
          'HTTP-Referer': 'https://memark.me',
          'X-Title': 'MeMark BYOK',
        },
        body: JSON.stringify({
          model: targetModel.includes('/') ? targetModel : `openrouter/${targetModel}`,
          messages: [{ role: 'user', content: 'Ping' }],
          max_tokens: 5,
        }),
      });

      const elapsed = Math.round(performance.now() - start);
      if (res.ok) {
        setPingStatus({ ms: elapsed, status: 'ok', msg: 'Verified connection' });
        playSound('success');
      } else {
        const errData = await res.json().catch(() => ({}));
        setPingStatus({ ms: elapsed, status: 'err', msg: errData?.error?.message || `HTTP ${res.status}` });
        playSound('click');
      }
    } catch (err) {
      const elapsed = Math.round(performance.now() - start);
      setPingStatus({ ms: elapsed, status: 'err', msg: err.message || 'Network error' });
    }
  };

  return (
    <div className="space-y-6">
      {/* 1. BYOK Primary Key Input Card */}
      <div
        className="p-5 rounded-2xl border space-y-4"
        style={{
          backgroundColor: surfaceBg,
          borderColor: apiKey ? 'rgba(16, 185, 129, 0.4)' : inputBorder,
          boxShadow: apiKey ? '0 0 20px rgba(16, 185, 129, 0.1)' : undefined,
        }}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-sm"
              style={{ backgroundColor: `${accent}20`, border: `1px solid ${accent}40` }}
            >
              <SafeIcon icon={FiKey} className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold" style={{ color: textColor }}>
                  Bring Your Own Key (BYOK)
                </h3>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                  Local-Only Storage
                </span>
              </div>
              <p className="text-xs" style={{ color: mutedText }}>
                Saved strictly in your browser's local storage. Never sent to any external database.
              </p>
            </div>
          </div>

          <a
            href="https://openrouter.ai/keys"
            target="_blank"
            rel="noreferrer"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
          >
            <span>Get OpenRouter Key</span>
            <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5" />
          </a>
        </div>

        {/* Input & Action Row */}
        <div className="space-y-2">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="sk-or-v1-... (OpenRouter / OpenAI / Anthropic key)"
                className="w-full rounded-xl px-3.5 py-2.5 text-xs font-mono border focus:outline-none transition-all pr-10"
                style={{
                  backgroundColor: inputBg,
                  borderColor: inputBorder,
                  color: textColor,
                }}
              />
              <button
                type="button"
                onClick={() => setShowKey(!showKey)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${
                  theme.isDark ? 'text-zinc-400 hover:text-white' : 'text-slate-400 hover:text-slate-900'
                }`}
                title={showKey ? 'Hide key' : 'Show key'}
              >
                <SafeIcon icon={showKey ? FiEyeOff : FiEye} className="w-4 h-4" />
              </button>
            </div>

            <button
              type="button"
              onClick={handleSaveApiKey}
              className="px-4 py-2.5 rounded-xl text-xs font-semibold bg-emerald-500 hover:bg-emerald-400 text-black transition-all cursor-pointer flex items-center gap-1.5 shrink-0 shadow-sm"
            >
              {keySaved ? (
                <>
                  <SafeIcon icon={FiCheck} className="w-4 h-4" />
                  <span>Saved!</span>
                </>
              ) : (
                <span>Save Key</span>
              )}
            </button>

            {apiKey && (
              <button
                type="button"
                onClick={handleClearApiKey}
                className="px-3 py-2.5 rounded-xl border border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs transition-all cursor-pointer"
                title="Clear key from local storage"
              >
                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex items-center justify-between text-[11px]" style={{ color: mutedText }}>
            <span>
              {apiKey
                ? '✓ Active BYOK key configured. All AI queries route strictly through your personal key.'
                : '⚠️ OpenRouter Key Required: Input your personal OpenRouter API key above to activate AI features. Your key is stored strictly on your local device.'}
            </span>
          </div>
        </div>
      </div>

      {/* 2. Connection Health & Latency Test */}
      <div
        className="flex items-center justify-between p-4 rounded-2xl border"
        style={{ backgroundColor: surfaceBg, borderColor: inputBorder }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
            style={{ backgroundColor: `${accent}15`, border: `1px solid ${accent}30` }}
          >
            <SafeIcon icon={FiCpu} className="w-4 h-4 text-emerald-400" />
          </div>
          <div>
            <p className="text-xs font-bold" style={{ color: textColor }}>
              Selected Model: <span className="font-mono text-emerald-300">{selectedModel}</span>
            </p>
            <p className="text-[11px]" style={{ color: mutedText }}>
              {pingStatus?.msg || 'Verify endpoint latency and model responsiveness'}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleTestPing}
          disabled={pingStatus === 'testing'}
          className="px-3.5 py-1.5 rounded-xl border text-xs font-mono font-semibold transition-all flex items-center gap-2 cursor-pointer hover:scale-105 shrink-0"
          style={{
            backgroundColor: `${accent}15`,
            borderColor: `${accent}40`,
            color: accent,
          }}
        >
          <SafeIcon icon={FiActivity} className={`w-3.5 h-3.5 ${pingStatus === 'testing' ? 'animate-spin' : ''}`} />
          <span>
            {pingStatus === 'testing'
              ? 'Testing...'
              : pingStatus
              ? `${pingStatus.ms}ms (${pingStatus.status.toUpperCase()})`
              : 'Test Connection'}
          </span>
        </button>
      </div>

      {/* 3. Preset Model Selection */}
      <div className="space-y-3">
        <p className="text-[10px] font-mono uppercase tracking-widest font-bold" style={{ color: mutedText }}>
          Select Intelligence Model
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {PRESET_MODELS.map((m) => {
            const isSelected = selectedModel === m.id;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => handleModelChange(m.id)}
                className={`p-3.5 rounded-xl border text-left transition-all flex items-start justify-between cursor-pointer ${
                  isSelected
                    ? 'border-emerald-500 bg-emerald-500/[0.08] shadow-[0_0_15px_rgba(16,185,129,0.15)]'
                    : theme.isDark
                    ? 'border-white/10 hover:border-white/20'
                    : 'border-slate-200 hover:border-slate-300 shadow-xs'
                }`}
                style={{ backgroundColor: isSelected ? undefined : cardBg }}
              >
                <div className="min-w-0 pr-2">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-xs font-bold truncate" style={{ color: isSelected ? textColor : mutedText }}>
                      {m.name}
                    </span>
                    <span
                      className={`text-[9px] font-mono px-1 py-0.2 rounded shrink-0 ${
                        theme.isDark
                          ? 'bg-white/5 text-zinc-400'
                          : 'bg-slate-100 text-slate-600 border border-slate-200'
                      }`}
                    >
                      {m.provider}
                    </span>
                  </div>
                  <p className="text-[11px] leading-relaxed line-clamp-2" style={{ color: mutedText }}>
                    {m.desc}
                  </p>
                </div>

                <div
                  className="w-4 h-4 rounded-full border flex items-center justify-center shrink-0 mt-0.5"
                  style={{
                    borderColor: isSelected ? accent : inputBorder,
                    backgroundColor: isSelected ? accent : 'transparent',
                  }}
                >
                  {isSelected && <SafeIcon icon={FiCheck} className="w-3 h-3 text-black font-extrabold" />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* 4. Custom Model ID input */}
      <div
        className="p-4 rounded-2xl border space-y-2"
        style={{ backgroundColor: surfaceBg, borderColor: inputBorder }}
      >
        <p className="text-xs font-bold" style={{ color: textColor }}>
          Custom Model Identifier
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={customModel}
            onChange={(e) => setCustomModel(e.target.value)}
            placeholder="e.g. anthropic/claude-3.5-sonnet or meta-llama/llama-3-8b"
            className="flex-1 rounded-xl px-3.5 py-2 text-xs font-mono border focus:outline-none transition-all"
            style={{
              backgroundColor: inputBg,
              borderColor: inputBorder,
              color: textColor,
            }}
          />
          <button
            type="button"
            onClick={handleSaveCustomModel}
            className={`px-3.5 py-2 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
              theme.isDark
                ? 'bg-white/10 hover:bg-white/20 text-white'
                : 'bg-slate-200 hover:bg-slate-300 text-slate-800'
            }`}
          >
            Use Model
          </button>
        </div>
      </div>

      {/* 5. Reasoning Chain Cleaner */}
      <div
        className="flex items-center justify-between p-3.5 rounded-xl border"
        style={{ backgroundColor: surfaceBg, borderColor: inputBorder }}
      >
        <div className="flex items-center gap-2.5">
          <SafeIcon icon={FiFilter} className="w-4 h-4 text-emerald-400" />
          <div>
            <p className="text-xs font-bold" style={{ color: textColor }}>
              Reasoning Chain Cleaner
            </p>
            <p className="text-[11px]" style={{ color: mutedText }}>
              Strips internal &lt;think&gt; chain-of-thought tags from model outputs
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleToggleStripThinking}
          className="px-3 py-1 rounded-lg text-xs font-mono font-semibold transition-all cursor-pointer"
          style={{
            backgroundColor: stripThinking ? `${accent}25` : 'rgba(255,255,255,0.05)',
            color: stripThinking ? accent : mutedText,
            border: `1px solid ${stripThinking ? `${accent}50` : inputBorder}`,
          }}
        >
          {stripThinking ? 'ON' : 'OFF'}
        </button>
      </div>
    </div>
  );
}
